import { useEffect, useState } from "react";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { sb } from "@/lib/sb";
import { buildSrcSet, pickSrc, type VariantMap } from "@/lib/image-srcset";
import { SafeImg } from "@/components/SafeImg";

interface SitePhotoProps {
  slotKey?: string;
  label: string;
  className?: string;
  imgClassName?: string;
  /**
   * Responsive `sizes` attribute. Defaults to full viewport width. Callers
   * should override with the actual rendered width at each breakpoint.
   */
  sizes?: string;
}

type Row = {
  webp_url: string | null;
  original_url: string | null;
  alt: string | null;
  variants: VariantMap;
};

// In-memory cache so the same slot doesn't re-fetch on every mount.
const cache = new Map<string, Row | null>();
const inflight = new Map<string, Promise<Row | null>>();

async function fetchSlot(slotKey: string): Promise<Row | null> {
  if (cache.has(slotKey)) return cache.get(slotKey)!;
  if (inflight.has(slotKey)) return inflight.get(slotKey)!;
  const p = sb
    .from("site_photos")
    .select("webp_url, original_url, alt, variants")
    .eq("slot_key", slotKey)
    .maybeSingle()
    .then(({ data, error }: { data: Row | null; error: { message: string } | null }) => {
      inflight.delete(slotKey);
      if (error) throw new Error(error.message);
      if (data) cache.set(slotKey, data);
      return data;
    })
    .catch((error: unknown) => {
      inflight.delete(slotKey);
      throw error;
    });
  inflight.set(slotKey, p);
  return p;
}

export function invalidateSitePhotoCache(slotKey?: string) {
  if (slotKey) cache.delete(slotKey);
  else cache.clear();
}

export function SitePhoto({ slotKey, label, className, imgClassName, sizes }: SitePhotoProps) {
  const [row, setRow] = useState<Row | null | undefined>(
    slotKey ? cache.get(slotKey) : null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let mounted = true;
    let retryId: ReturnType<typeof setTimeout> | undefined;
    setFailed(false);
    if (!slotKey) return;
    if (cache.has(slotKey)) {
      setRow(cache.get(slotKey)!);
      return;
    }
    const load = () => {
      fetchSlot(slotKey)
        .then((r) => {
          if (!mounted) return;
          setRow(r);
          if (!r) retryId = setTimeout(load, 5000);
        })
        .catch(() => {
          if (mounted) retryId = setTimeout(load, 3000);
        });
    };
    load();
    return () => {
      mounted = false;
      if (retryId) clearTimeout(retryId);
    };
  }, [slotKey]);

  const hasImage = !!(row && (row.webp_url || row.original_url));

  if (hasImage && !failed) {
    const fallbackUrl = (row!.webp_url || row!.original_url)!;
    const srcSet = buildSrcSet(row!.variants);
    const src = pickSrc(row!.variants, fallbackUrl);
    return (
      <SafeImg
        src={src}
        {...(srcSet ? { srcSet, sizes: sizes || "100vw" } : {})}
        alt={row!.alt || label}
        loading="lazy"
        decoding="async"
        width={1600}
        height={1200}
        className={cn("w-full h-full object-cover", imgClassName)}
        onFail={() => setFailed(true)}
      />
    );
  }


  return (
    <div
      className={cn(
        "w-full h-full bg-gradient-to-br from-neutral-300 to-neutral-400 flex flex-col items-center justify-center text-neutral-700 p-4",
        className,
      )}
      role="img"
      aria-label={label}
    >
      <ImageIcon className="h-8 w-8 mb-2 opacity-60" />
      <span className="text-xs font-mono opacity-70 text-center">{label}</span>
    </div>
  );
}
